// FIXME - When modal has no click-off, a click-off attempt results in the focused button losing
// focus, which then means the *next* button is auto focused (which we actually want to happen,
// but only when the button has lost focus because of a tab key press). Solution will probably
// involve checking whether (a) the modal wrap itself is focused & (b) no buttons are focused,
// before focusing the next button. Might need a short timeout.

import { each } from "lodash";

declare const jQuery: any;
const $ = jQuery;

let closeTimer: any;
const modalCloseTimeoutDefault = 5000;

export interface ModalSettings
{
    title: string;
    type?: "progress"|"prompt"|"warning-prompt"|"success"|"error";
    heading?: string;
    body?: string;
    buttons?: ModalButton[];
    className?: string;
    closeTimeout?: number|true; // milliseconds after which modal auto-closes
    allowClickOff?: boolean; // default false
    onClickOff?: () => void; // overrides allowClickOff; if set, modal does not auto close, so must be handled in calling code
}

export interface ModalButton
{
    onClick: (clickEvent?, button?, $modal?) => void;
    label: string;
    className?: string;
}

// Returns jQuery for modal
function createModal(modal: ModalSettings)
{
    let cls: string = "modal-box";
    if (modal.type)
    {
        cls += " modal-type-" + modal.type;
    }
    if (modal.className)
    {
        cls += " " + modal.className;
    }
    let $modal: any;
    const title: string = modal.title || "";
    const heading: string = modal.heading ? `<div class="modal-heading">${modal.heading}</div>` : "";
    const body: string = modal.body ? `<div class="modal-body">${modal.body}</div>` : "";
    let $buttonsWrap: any;
    if (modal.buttons && modal.buttons.length > 0)
    {
        $buttonsWrap = $(`<div class="modal-buttons">`);
        const getNextButton = ($button) => {
            const $buttons = $buttonsWrap.find(".modal-button");
            if ($buttons.length < 2)
            {
                return $button;
            }
            let $buttonWrap = $button.closest(".modal-button-wrap").next(".modal-button-wrap");
            if ($buttonWrap.length === 0)
            {
                $buttonWrap = $buttonsWrap.find(".modal-button-wrap").first();
            }
            return $buttonWrap.find(".modal-button");
        };
        each(modal.buttons, (button, buttonIndex: number) => {
            let buttonClass: string = `button modal-button`;
            if (button.className)
            {
                buttonClass += " " + button.className;
            }
            const $button = $(`<button class="${buttonClass}" tabindex="-1">${button.label}</button>`);
            $button.on("click", function (event) {
                event.stopPropagation();
                button.onClick(event, button, $modal);
            });
            $(`<div class="modal-button-wrap">`).append($button).appendTo($buttonsWrap);
            $button.on("focusout", function (event) {
                console.log("focusout",this,"next btn",getNextButton($button)[0]);//fixme
                getNextButton($button).trigger("focus");
            });
        });
    }
    const html: string = `<div class="${cls}" tabindex="-1"><div class="modal-outer">
        <div class="modal-inner">
            <div class="modal-title">${title}</div>
            <div class="modal-main">${heading}${body}</div>
        </div>
    </div></div>`;
    $modal = $(html);
    if ($buttonsWrap)
    {
        $modal.find(`.modal-main`).append($buttonsWrap);
    }
    $modal.find(".modal-inner").on("click", function (event) {
        event.stopPropagation();
    });
    const onClickOff: (() => void | undefined) = modal.onClickOff || (modal.allowClickOff && (() => { $modal.trigger("close"); }));
    if (onClickOff)
    {
        $modal.on("click", function (event) {
            event.stopPropagation();
            onClickOff();
        }).on("keydown", function (event) {
            event.stopPropagation();
            if (event.originalEvent.key === "Escape")
            {
                onClickOff();
            }
        });
    }
    $modal.on("open", function (event) {
        event.stopPropagation();
        $modal.appendTo(document.body).trigger("focus");
        $modal.find(".modal-button").first().trigger("focus");
    }).on("close", function (event) {
        event.stopPropagation();
        $modal.remove();
    });
    return $modal;
}

// Returns jQuery for modal
export function openModal(modal: ModalSettings)
{
    clearTimeout(closeTimer);
    closeModal();
    const $modal = createModal(modal);
    if (modal.closeTimeout)
    {
        const t: number = modal.closeTimeout === true ?
            modalCloseTimeoutDefault :
            modal.closeTimeout;
        closeTimer = setTimeout(closeModal, t);
    }
    return $modal.trigger("open");
}

export function closeModal(): void
{
    $(`.modal-box`).trigger("close");
}

// Returned Promise resolves with true if user clicks OK, false if user clicks Cancel.
export function confirmModal(title: string, body: string, buttonLabelType?: "yesno"|"okcancel"): Promise<boolean>
{
    let okLabel: string = "OK";
    let cancelLabel: string = "Cancel";
    if (!body)
    {
        body = title;
        title = `Confirm`;
    }
    if (buttonLabelType === "yesno")
    {
        okLabel = "Yes";
        cancelLabel = "No";
    }
    return new Promise<boolean>((resolve, reject) => {
        const onOk = () => {
            closeModal();
            resolve(true);
        };
        const onCancel = () => {
            closeModal();
            resolve(false);
        };
        const modalSettings: ModalSettings = {
            title,
            type: "prompt",
            body,
            buttons: [
                { onClick: onOk, label: okLabel, className: "ok" },
                { onClick: onCancel, label: cancelLabel, className: "cancel" },
            ],
            onClickOff: onCancel
        };
        openModal(modalSettings);
    });
}

export function waitModal(title: string, body?: string)
{
    if (!body)
    {
        body = title;
        title = `Please wait...`;
    }
    const modalSettings: ModalSettings = {
        title,
        type: "progress",
        body,
        allowClickOff: false,
    };
    return openModal(modalSettings);
}

export function progressModal(title: string, body: string)
{
    if (!body)
    {
        body = title;
        title = `Please wait...`;
    }
    body = body || "";
    body += `<div class="modal-progress-bar"><progress></progress></div>`;
    const modalSettings: ModalSettings = {
        title,
        type: "progress",
        body,
        allowClickOff: false,
    };
    return openModal(modalSettings);
}

export function successModal(title: string, body?: string, closeTimeout?: number)
{
    if (!body)
    {
        body = title;
        title = `Success`;
    }
    const modalSettings: ModalSettings = {
        title,
        type: "success",
        body,
        allowClickOff: true,
        closeTimeout,
        buttons: [
            { onClick: closeModal, label: "OK", className: "primary" },
        ],
    };
    return openModal(modalSettings);
}

export function errorModal(title: string, body?: string)
{
    if (!body)
    {
        body = title;
        title = `Error`;
    }
    const modalSettings: ModalSettings = {
        title,
        type: "error",
        body,
        allowClickOff: true,
        buttons: [
            { onClick: closeModal, label: "OK", className: "primary" },
        ],
    };
    return openModal(modalSettings);
}

if (!window["iw"])
{
    window["iw"] = {};
}

window["iw"].modal = {
    openModal,
    closeModal,
    confirmModal,
    waitModal,
    progressModal,
    successModal,
    errorModal
};
